import type { Address } from "viem"
import type { Config } from "wagmi"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import { EventEmitter, makeGroupId } from "@/domains/library"
import { LockMergedEvent } from "@/domains/lock/events"

import type { IMergeNftActions } from "./interfaces"

interface MergeNftInteractionParams {
  account: Address
  chainId: number
  config: Config
  vestedAddress: Address
  fromTokenId: number
  toTokenId: number
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
  onChainActions: IMergeNftActions
}

export const mergeNftInteraction = async ({
  account,
  chainId,
  config,
  vestedAddress,
  fromTokenId,
  toTokenId,
  emitter,
  eventBus,
  onChainActions,
}: MergeNftInteractionParams) => {
  // Generate a unique group ID for this interaction instance
  const groupId = makeGroupId("merge-nft")

  if (!fromTokenId || !toTokenId) {
    emitter.emit("merge-nft", {
      title: "Unable to merge NFTS",
      description: "No NFT selected to merge",
      variant: "info",
    })
    return false
  }

  if (fromTokenId === toTokenId) {
    emitter.emit("merge-nft", {
      title: "Unable to merge NFTS",
      description: "Can not merge the same NFTs",
      variant: "info",
    })
    return false
  }

  const result = await onChainActions.merge({
    account,
    chainId,
    config,
    vestedAddress,
    fromTokenId,
    toTokenId,
  })

  if (result.success) {
    eventBus.publish(new LockMergedEvent(groupId, fromTokenId, toTokenId, result.tx))
    emitter.emit("merge-nft", {
      title: "Merge NFT",
      description: `Merged #${fromTokenId} into ${toTokenId}`,
      variant: "success",
    })
    return result
  }

  if (result.success === false) {
    emitter.emit("error-merging-nft", {
      title: "Merge NFT failed",
      description: `Unable to merge NFT`,
      variant: "error",
    })
    return false
  }

  return result
}
